import React, { useEffect } from 'react';
import { Box, Button, Container, Heading, Stack, Text, VStack } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router';
import FormInput from '@/components/Inputs/FormInput';
import { loginSchema, LoginFormValues } from '@/schemas/auth';
import { useLogin } from '@/hooks/useLogin';
import { useAuthStatus } from '@/hooks/useAuthStatus';

const LoginView: React.FC = () => {
  const navigate = useNavigate();
  const { data: isAuthenticated } = useAuthStatus();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
  });

  const { login: loginMutate, isLoading: isLoginPending, isError: isLoginError } = useLogin();

  // Redirect to admin if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin');
    }
  }, [isAuthenticated, navigate]);

  const onSubmit = (data: LoginFormValues) => {
    loginMutate(data);
  };

  return (
    <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg="bg.subtle">
      <Container maxW="md">
        <Box
          bg="bg"
          borderRadius="xl"
          borderWidth="1px"
          borderColor="border"
          p={{ base: '6', md: '8' }}
          shadow="sm"
        >
          <Stack gap="8">
            {/* Header */}
            <VStack gap={{ base: '2', md: '3' }} textAlign="center">
              <Heading size={{ base: 'xl', md: '2xl' }} fontWeight="bold" letterSpacing="tight">
                Logga in
              </Heading>
              <Text color="fg.muted" fontSize={{ base: 'sm', md: 'md' }}>
                Välkommen tillbaka till Palatium Fastigheter
              </Text>
            </VStack>

            {/* Login Form */}
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack gap="6">
                <Stack gap="5">
                  <FormInput
                    name="email"
                    label="E-postadress"
                    type="email"
                    register={register}
                    errors={errors}
                    required
                    placeholder="din.email@example.com"
                  />

                  <FormInput
                    name="password"
                    label="Lösenord"
                    type="password"
                    register={register}
                    errors={errors}
                    required
                    placeholder="Ange ditt lösenord"
                  />
                </Stack>

                {/* Error Alert */}
                {isLoginError && (
                  <Box
                    p={3}
                    bg="red.subtle"
                    borderWidth="1px"
                    borderColor="red.muted"
                    borderRadius="md"
                    color="red.fg"
                  >
                    <Text fontSize="sm" fontWeight="medium">
                      Inloggning misslyckades. Kontrollera dina uppgifter och försök igen.
                    </Text>
                  </Box>
                )}

                <Button
                  type="submit"
                  colorPalette="blue"
                  size="lg"
                  w="full"
                  loading={isLoginPending}
                  mt="2"
                >
                  Logga in
                </Button>
              </Stack>
            </form>

            {/* Footer Text */}
            <Text textAlign="center" fontSize="xs" color="fg.muted">
              Endast för behöriga användare
            </Text>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default LoginView;
